import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, AlertCircle, FileText, Mail, Phone, MapPin, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDateTime } from '@/lib/utils-helpers';

export default function PendingVendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, changes_requested, all
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [reviewAction, setReviewAction] = useState('approve');
  const [reviewReason, setReviewReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchVendors();
  }, [filter, page]);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/vendors/pending-review?status=${filter}&page=${page}&limit=10`);
      setVendors(res.data.vendors || []);
      setPagination(res.data.pagination || {});
    } catch (err) {
      console.error(err);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (reviewAction !== 'approve' && !reviewReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(
        `/admin/vendors/${selectedVendor.id}/review?action=${reviewAction}${
          reviewReason ? `&reason=${encodeURIComponent(reviewReason)}` : ''
        }`
      );
      toast.success(`Vendor ${reviewAction.replace('_', ' ')} successfully`);
      setReviewDialog(false);
      setReviewReason('');
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Review failed');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: { variant: 'default', label: 'Pending Review', color: 'bg-amber-100 text-amber-800' },
      changes_requested: { variant: 'warning', label: 'Changes Requested', color: 'bg-orange-100 text-orange-800' },
      approved: { variant: 'success', label: 'Approved', color: 'bg-emerald-100 text-emerald-800' },
      rejected: { variant: 'destructive', label: 'Rejected', color: 'bg-red-100 text-red-800' },
    };
    const config = map[status] || map.pending;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading && vendors.length === 0) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">
            Pending Vendor Review
          </h1>
          <p className="text-muted-foreground mt-1">Review and approve vendor applications</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['pending', 'changes_requested', 'all'].map(f => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setFilter(f); setPage(1); }}
          >
            {f.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </Button>
        ))}
      </div>

      {/* Vendors List */}
      <div className="space-y-4">
        {vendors.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">No vendors found</p>
            </CardContent>
          </Card>
        ) : (
          vendors.map(vendor => (
            <Card key={vendor.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{vendor.name}</CardTitle>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {vendor.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDateTime(vendor.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {getStatusBadge(vendor.review_status)}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {vendor.vendor_profile && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Business Type:</span>
                      <p className="font-medium">{vendor.vendor_profile.category || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Address:</span>
                      <p className="font-medium truncate">{vendor.vendor_profile.address || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Onboarding:</span>
                      <p className="font-medium">Step {vendor.onboarding_step}/7</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status:</span>
                      <p className="font-medium">{vendor.onboarding_status}</p>
                    </div>
                  </div>
                )}

                {vendor.review_reason && (
                  <Alert className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Review Notes:</strong> {vendor.review_reason}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      setSelectedVendor(vendor);
                      setReviewAction('approve');
                      setReviewDialog(true);
                    }}
                    data-testid={`approve-vendor-${vendor.id}`}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedVendor(vendor);
                      setReviewAction('request_changes');
                      setReviewDialog(true);
                    }}
                  >
                    <AlertCircle className="h-4 w-4 mr-1" />
                    Request Changes
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      setSelectedVendor(vendor);
                      setReviewAction('reject');
                      setReviewDialog(true);
                    }}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.pages} ({pagination.total} total)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= pagination.pages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={reviewDialog} onOpenChange={setReviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewAction === 'approve' && 'Approve Vendor'}
              {reviewAction === 'request_changes' && 'Request Changes'}
              {reviewAction === 'reject' && 'Reject Vendor'}
            </DialogTitle>
            <DialogDescription>
              {selectedVendor?.name} ({selectedVendor?.email})
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {reviewAction === 'approve' ? (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  This vendor will be approved and their store will be visible to customers.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-2">
                <Label>Reason / Feedback *</Label>
                <Textarea
                  value={reviewReason}
                  onChange={e => setReviewReason(e.target.value)}
                  placeholder={reviewAction === 'reject' 
                    ? 'Explain why the application was rejected...'
                    : 'Specify what changes are needed...'}
                  rows={4}
                  required
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleReview}
              disabled={submitting || (reviewAction !== 'approve' && !reviewReason.trim())}
              variant={reviewAction === 'approve' ? 'default' : reviewAction === 'reject' ? 'destructive' : 'default'}
            >
              {submitting ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
